import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Users,
  Heart,
  Sparkles,
  ExternalLink,
  Mail,
  Facebook,
  Instagram
} from 'lucide-react';
import usePageBanner from '../hooks/usePageBanner';
import TranslatedContent from '../components/TranslatedContent';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { getParishPage } from '../services/api';

const UngdomarPage = () => {
  const { t, language, isRTL } = useLanguage();
  const { bannerImage } = usePageBanner('ungdomar', language);
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dynamic meeting dates from CMS
  const [termName, setTermName] = useState('Vår-terminen 2026');
  const [meetingDates, setMeetingDates] = useState(['10/04', '24/04', '08/05', '22/05']);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await getParishPage('ungdomar');
        if (data) {
          setCmsContent(data);
          // Load dynamic meeting dates if available
          if (data.term_name) {
            setTermName(data.term_name);
          }
          if (data.meeting_dates && data.meeting_dates.length > 0) {
            setMeetingDates(data.meeting_dates);
          }
        }
      } catch (error) {
        // Using default content when CMS content not available
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Contact links
  const contacts = [
    { type: 'facebook', label: 'JKUF – Facebook', url: 'https://www.facebook.com/profile.php?id=61557875646036' },
    { type: 'document', label: 'Kristus lever (PDF)', url: 'https://www.katolskakyrkan.se/media/3966/christusvivitpdf_191203.pdf' },
    { type: 'email', label: 'jkuf@fatimakrona.se', url: 'mailto:jkuf@fatimakrona.se' },
    { type: 'instagram', label: 'jkuf.blekinge', url: 'https://www.instagram.com/jkuf.blekinge' }
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="relative h-72 overflow-hidden">
        <img
          src={bannerImage || 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920'}
          alt="Ungdomar"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link 
              to="/churches" 
              className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <AutoTranslate>Tillbaka till Kyrkor</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title-sm font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Ungdomar</AutoTranslate>
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              <AutoTranslate>Katolsk ungdomsverksamhet i Blekinge</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* JKUF Intro Card */}
          <Card className="border-0 shadow-xl overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-serif font-bold text-white">
                  Jacintas Katolska Ungdoms Förening (JKUF)
                </h2>
              </div>
            </div>
            <CardContent className="p-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                <AutoTranslate>Jacintas Katolska Ungdomsförening (JKUF) är en ungdomsgrupp som är grundad 2019, och har till uppgift att samla katolska ungdomar i Blekinge Län.</AutoTranslate>
              </p>
            </CardContent>
          </Card>

          {/* CMS Content (if available) */}
          {cmsContent && cmsContent.content && (
            <Card className="mb-8 border-0 shadow-xl overflow-hidden">
              <CardContent className="p-8">
                <TranslatedContent 
                  content={{ sv: cmsContent.content }} 
                  as="div" 
                  className="prose prose-lg max-w-none"
                  html
                />
              </CardContent>
            </Card>
          )}

          {/* Term Dates Card */}
          <Card className="border-0 shadow-xl overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-white" />
                <h3 className="text-xl font-serif font-bold text-white">
                  <AutoTranslate>{termName}</AutoTranslate>
                </h3>
              </div>
            </div>
            <CardContent className="p-6">
              {meetingDates.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {meetingDates.map((date, idx) => (
                    <div 
                      key={`meeting-date-${date}-${idx}`}
                      className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 text-center border border-blue-200"
                    >
                      <span className="text-lg font-bold text-blue-700">{date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  <AutoTranslate>Inga mötesdatum tillgängliga ännu</AutoTranslate>
                </p>
              )}
            </CardContent>
          </Card>


          {/* Contact Section */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
              <div className="flex items-center gap-3">
                <Heart className="w-8 h-8 text-white" />
                <h3 className="text-xl font-serif font-bold text-white">
                  <AutoTranslate>Kontakta oss</AutoTranslate>
                </h3>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contacts.map((contact, idx) => (
                  <a
                    key={`contact-${contact.type}-${contact.label || idx}`}
                    href={contact.url}
                    target={contact.type !== 'email' ? '_blank' : undefined}
                    rel={contact.type !== 'email' ? 'noopener noreferrer' : undefined}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                  >
                    {contact.type === 'facebook' && <Facebook className="w-5 h-5 text-blue-600" />}
                    {contact.type === 'instagram' && <Instagram className="w-5 h-5 text-pink-600" />}
                    {contact.type === 'email' && <Mail className="w-5 h-5 text-green-600" />}
                    {contact.type === 'document' && <ExternalLink className="w-5 h-5 text-purple-600" />}
                    <span className="text-gray-700 group-hover:text-gray-900">
                      {contact.label}
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UngdomarPage;
